import React from 'react';
import Card from '../../../components/Card';
import { TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Badge from '../../../components/Badge';
import { faChevronLeft, faClock, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import moment from 'moment';

interface Props {
    activity: PolicePatrolSchedule;
    onEdit: () => void;
    onFinalize?: () => void;
}

export default function PolicePatrolScheduleCard({ activity, onEdit, onFinalize }: Props) {
    const { styles, theme } = useStyles(styleSheet);

    const isOpen = !activity.endDate;
    const hasRas = (!!activity.activityAndServices && activity.activityAndServices.length > 0);
    const hasOccurrence = (!!activity.occurrences && activity.occurrences.length > 0);
    const attachmentsCount = activity.attachments?.length ?? 0;

    return (
        <Card>
            <TouchableOpacity onPress={onEdit}>
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ flexDirection: 'column', flex: 1 }}>
                        <View style={styles.overlineRow}>
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                fontSize={11}
                                color={isOpen ? theme.colors.badges.orange.textColor : theme.colors.neutral['400']}
                            />
                            <MyAppText style={[styles.overlineText, isOpen && styles.overlineTextOpen]}>
                                {translate('policePatrolScheduleAcronym')}{isOpen ? ` · ${translate('inProgress')}` : ''}
                            </MyAppText>
                        </View>

                        <MyAppText style={styles.address} numberOfLines={1}>
                            {activity.address}
                            {activity.number ? `, ${activity.number}` : ''}
                        </MyAppText>

                        <View style={styles.timeRow}>
                            <FontAwesomeIcon icon={faClock} fontSize={14} color={theme.colors.neutral['500']} />
                            <MyAppText style={styles.timeText}>
                                {isOpen
                                    ? `${translate('start')} ${moment(activity.startDate).format('HH:mm')}`
                                    : `${moment(activity.startDate).format('HH:mm')} – ${moment(activity.endDate).format('HH:mm')}`}
                                {!isOpen && activity.odometer ? ` · km ${activity.odometer}` : ''}
                            </MyAppText>
                        </View>

                        <View style={styles.bottomRow}>
                            {hasRas && (
                                <Badge mode='ROUNDED' color='purple' textColor={theme.colors.badges.purple.textColor}>
                                    {`${activity.activityAndServices.length} ${translate('activityAndService')}`}
                                </Badge>
                            )}
                            {hasOccurrence && (
                                <Badge mode='ROUNDED' color='red' textColor={theme.colors.badges.red.textColor}>
                                    {`${activity.occurrences?.length} ${translate(activity.occurrences?.length === 1 ? 'occurrence' : 'occurrences')}`}
                                </Badge>
                            )}
                            {attachmentsCount > 0 && (
                                <Badge mode='ROUNDED' color='green' textColor={theme.colors.badges.green.textColor}>
                                    {attachmentsCount} {attachmentsCount === 1 ? translate('attachment') : translate('attachments')}
                                </Badge>
                            )}
                        </View>
                    </View>

                    {isOpen && onFinalize && (
                        <TouchableOpacity
                            style={styles.finalizeRow}
                            onPress={(e) => {
                                e.stopPropagation();
                                onFinalize();
                            }}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} fontSize={11} color={theme.colors.primary['500']} />
                            <MyAppText style={styles.finalizeText}>{translate('finalize')}</MyAppText>
                        </TouchableOpacity>
                    )}
                </View>
            </TouchableOpacity>
        </Card>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    overlineRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        marginBottom: 6,
    },
    overlineText: {
        fontSize: 11,
        fontWeight: '600',
        letterSpacing: 0.3,
        color: theme.colors.neutral['400'],
        textTransform: 'uppercase',
    },
    overlineTextOpen: {
        color: theme.colors.badges.orange.textColor,
    },
    address: {
        fontSize: 15,
        fontWeight: '600',
        color: theme.colors.field.text,
        marginBottom: 6,
    },
    timeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    timeText: {
        fontSize: 13,
        color: theme.colors.neutral['500'],
    },
    finalizeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
        gap: 4,
        height: '100%'
    },
    finalizeText: {
        fontSize: 13,
        fontWeight: '600',
        color: theme.colors.primary['500'],
    },
    bottomRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
        marginTop: 10,
    },
}));
